import React, { useEffect, useState } from 'react';
import Logo from './Logo';

const PRIMARY = '#3498db';
const NAVY = '#0F172A';

const SocialIcon = ({ href, children }: { href: string; children: React.ReactNode }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 34, height: 34, borderRadius: 8,
        background: hovered ? 'rgba(52,152,219,0.18)' : 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.1)',
        transition: 'background 0.2s ease',
        color: hovered ? '#3498db' : '#94a3b8',
        textDecoration: 'none',
      }}
    >
      {children}
    </a>
  );
};

const Footer: React.FC = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 940);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 940);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <footer style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1e3a5f 60%, #1a6fa8 100%)', color: '#94a3b8', fontFamily: "'Inter', sans-serif" }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '48px 20px 0' : '64px 32px 0' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr 1fr', gap: isMobile ? 36 : 48, marginBottom: 48 }}>

          {/* Brand column */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <Logo size={44} showName={false} bgColor={PRIMARY} boxShadow="none" imageScale={0.92} innerPadding={0.06} />
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>Instaship</div>
                <div style={{ fontSize: 11, color: PRIMARY, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Logistics Solutions</div>
              </div>
            </div>
            <p style={{ fontSize: 14, lineHeight: 1.7, color: '#94a3b8', marginBottom: 20, maxWidth: 280 }}>
              Revolutionizing logistics with technology-driven solutions for businesses of all sizes.
            </p>
            {/* Social icons */}
            <div style={{ display: 'flex', gap: 10 }}>
              <SocialIcon href="https://facebook.com">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>
              </SocialIcon>
              <SocialIcon href="https://twitter.com">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"/></svg>
              </SocialIcon>
              <SocialIcon href="https://linkedin.com">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg>
              </SocialIcon>
              <SocialIcon href="https://instagram.com">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
              </SocialIcon>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 18 }}>Services</h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {['Domestic Shipping', 'Estimate Price', 'Live Tracking', 'Verified Transporters'].map(item => (
                <li key={item} style={{ fontSize: 13, color: '#94a3b8' }}>{item}</li>
              ))}
            </ul>
          </div>

          {/* Pickup Cities */}
          <div>
            <h4 style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 18 }}>Pickup Cities</h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <li style={{ fontSize: 13, color: '#94a3b8' }}>Delhi NCR</li>
              <li style={{ fontSize: 13, color: '#94a3b8' }}>&amp; More Coming Soon</li>
              <li style={{ marginTop: 8 }}><a href="/terms-of-service" style={{ fontSize: 13, color: '#94a3b8', textDecoration: 'none' }}>Terms of Service</a></li>
              <li><a href="/privacy-policy" style={{ fontSize: 13, color: '#94a3b8', textDecoration: 'none' }}>Privacy Policy</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 18 }}>Contact</h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <li>
                <a href="mailto:support@instaship.co.in" style={{ fontSize: 13, color: '#94a3b8', textDecoration: 'none' }}>
                  support@instaship.co.in
                </a>
              </li>
              <li>
                <a href="tel:+918360552876" style={{ fontSize: 13, color: '#94a3b8', textDecoration: 'none' }}>
                  +91-8360552876
                </a>
              </li>
              <li>
                <a href="tel:+918800300788" style={{ fontSize: 13, color: '#94a3b8', textDecoration: 'none' }}>
                  +91-8800300788
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', padding: isMobile ? '16px 20px' : '20px 32px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <p style={{ fontSize: 12, color: '#94a3b8', margin: 0, textAlign: 'center' }}>
            © {new Date().getFullYear()} Instaship Logistics Private Limited. All rights reserved.
          </p>
          <p style={{ fontSize: 12, color: '#94a3b8', margin: 0, textAlign: 'center' }}>
            10/14, Old Rajinder Nagar, New Delhi, Delhi -110060
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
